import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { HomeComponent } from "./home/home.component";
import { PageNotFoundComponent } from "./error-routing/not-found/not-found.component";
import { UncaughtErrorComponent } from "./error-routing/error/uncaught-error.component";
import { ErrorRoutingModule } from "./error-routing/error-routing.module";
import { LoginComponent } from "./shared/modules/login/login.component";
import { PasswordRecoveryComponent } from "./shared/modules/password-recovery/password-recovery.component";
import { WelcomeComponent } from "./welcome/welcome.component";

import { AuthGuard } from "./shared";
import { TranslateService } from "@ngx-translate/core";

// canActivate: [AuthGuard]
export let routes: Routes = [
  { path: "", redirectTo: "welcome", pathMatch: "full" },
  {
    path: "welcome",
    component: WelcomeComponent,
  },
  {
    path: "home",
    component: HomeComponent,
    data: {
      code: "home_dashboard",
      text: "Home",
      iconName: "fa-home",
      iconFontSet: "fa",
    },
    canActivate: [AuthGuard],
  },
  { path: "error", component: UncaughtErrorComponent },
  { path: "login", component: LoginComponent, data: { text: "login" } },
  {
    path: "passwordRecovery",
    component: PasswordRecoveryComponent,
    data: { text: "passwordRecovery" },
  },
  {
    path: "customers",
    loadChildren: () =>
      import("./layout-modules/customers-Management/customers.module").then(
        (m) => m.CustomersModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "customer_management",
      text: "Customers",
      iconName: "fa-users",
      iconFontSet: "fa",
    },
  },
  {
    path: "employees",
    loadChildren: () =>
      import("./layout-modules/employees-Management/employees.module").then(
        (m) => m.EmployeesModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "employee_management",
      text: "Employees",
      iconName: "fa-id-badge",
      iconFontSet: "fa",
    },
  },
  {
    path: "suppliers",
    loadChildren: () =>
      import("./layout-modules/suppliers-Management/suppliers.module").then(
        (m) => m.SuppliersModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "supplier_management",
      text: "Suppliers",
      iconName: "fa-boxes",
      iconFontSet: "fa",
    },
  },
  {
    path: "contracts",
    loadChildren: () =>
      import("./layout-modules/contracts-Management/contracts.module").then(
        (m) => m.ContractsModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "contract_management",
      text: "Contracts",
      iconName: "fa-file-alt",
      iconFontSet: "fa",
    },
  },
  {
    path: "vehicles",
    loadChildren: () =>
      import("./layout-modules/vehicles-Management/vehicles.module").then(
        (m) => m.VehiclesModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "vehicle_management",
      text: "Vehicles",
      iconName: "fa-truck",
      iconFontSet: "fa",
    },
  },
  {
    path: "cards",
    loadChildren: () =>
      import("./layout-modules/cards-Management/cards.module").then(
        (m) => m.CardsModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "card_management",
      text: "Cards",
      iconName: "fa-id-card",
      iconFontSet: "fa",
    },
  },
  {
    path: "properties",
    loadChildren: () =>
      import("./layout-modules/properties-Management/property.module").then(
        (m) => m.PropertiesModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "property_management",
      text: "Properties",
      iconName: "fa-clipboard-list",
      iconFontSet: "fa",
    },
  },
  {
    path: "prompts",
    loadChildren: () =>
      import("./layout-modules/prompt-Management/prompt.module").then(
        (m) => m.PromptModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "card_prompt_management",
      text: "Card Prompts",
      iconName: "fa-terminal",
      iconFontSet: "fa",
    },
  },
  {
    path: "categories",
    loadChildren: () =>
      import("./layout-modules/categories-Management/category.module").then(
        (m) => m.CategoryModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "category_management",
      text: "Categories",
      iconName: "fa-folder-open",
      iconFontSet: "fa",
    },
  },
  {
    path: "users",
    loadChildren: () =>
      import("./layout-modules/users-Management/users.module").then(
        (m) => m.UsersModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "user_management",
      text: "Users",
      iconName: "fa-user",
      iconFontSet: "fa",
    },
  },
  {
    path: "reports-generation",
    loadChildren: () =>
      import(
        "./layout-modules/reports-generation-Management/reports-generation.module"
      ).then((m) => m.ReportsGenerationModule),
    canActivate: [AuthGuard],
    data: {
      code: "report_generation",
      text: "Reports Generation",
      iconName: "fa-file-signature",
      iconFontSet: "fa",
    },
  },
  {
    path: "reports",
    loadChildren: () =>
      import("./layout-modules/reports-Management/reports.module").then(
        (m) => m.ReportsModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "report_history",
      text: "Reports",
      iconName: "fa-clipboard-list",
      iconFontSet: "fa",
    },
  },
  {
    path: "movements",
    loadChildren: () =>
      import("./layout-modules/movements-Management/movements.module").then(
        (m) => m.MovementsModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "contract_movements",
      text: "Movements",
      iconName: "fa-chart-line",
      iconFontSet: "fa",
    },
  },
  {
    path: "pos-movement-headers",
    loadChildren: () =>
      import("./layout-modules/movements2-Management/movements2.module").then(
        (m) => m.Movements2Module
      ),
    canActivate: [AuthGuard],
    data: {
      code: "document_management",
      text: "POS Transaction",
      iconName: "fa-list",
      iconFontSet: "fa",
    },
  },
  {
    path: "sale-details",
    loadChildren: () =>
      import("./layout-modules/movements3-Management/movements3.module").then(
        (m) => m.Movements3Module
      ),
    canActivate: [AuthGuard],
    data: {
      code: "sale_details",
    },
  },
  {
    path: "professionalFuel",
    loadChildren: () =>
      import(
        "./layout-modules/professional-fuel-Management/professional-fuel.module"
      ).then((m) => m.ProfessionalFuelModule),
    canActivate: [AuthGuard],
    data: {
      code: "professional_fuel_pt",
      text: "Professional Fuel",
      iconName: "fa-chart-line",
      iconFontSet: "fa",
    },
  },
  {
    path: "professionalFuelEs",
    loadChildren: () =>
      import(
        "./layout-modules/professional-fuel-es-Management/professional-fuel-es.module"
      ).then((m) => m.ProfessionalFuelEsModule),
    canActivate: [AuthGuard],
    data: {
      code: "professional_fuel_es",
      text: "Professional Fuel Es",
      iconName: "fa-chart-line",
      iconFontSet: "fa",
    },
  },
  {
    path: "deliveries",
    loadChildren: () =>
      import("./layout-modules/delivery-Management/deliveries.module").then(
        (m) => m.DeliveryModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "stock_deliveries",
      text: "FuelDeliveries",
      iconName: "fa-chart-line",
      iconFontSet: "fa",
    },
  },
  {
    path: "fuelStocks",
    loadChildren: () =>
      import("./layout-modules/fuel-stock-Management/fuel-stocks.module").then(
        (m) => m.FuelStocksModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "stock_management",
      text: "fa-tachometer-alt",
      iconName: "fa-chart-line",
      iconFontSet: "fa",
    },
  },
  {
    path: "sites",
    loadChildren: () =>
      import("./layout-modules/sites-Management/sites.module").then(
        (m) => m.SitesModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "site_management",
      text: "fa-building",
      iconName: "fa-building",
      iconFontSet: "fa",
    },
  },
  {
    path: "products",
    loadChildren: () =>
      import("./layout-modules/products-Management/product.module").then(
        (m) => m.ProductModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "product_management",
      text: "fa-building",
      iconName: "fa-building",
      iconFontSet: "fa",
    },
  },
  {
    path: "families",
    loadChildren: () =>
      import(
        "./layout-modules/product-family-Management/product-family.module"
      ).then((m) => m.ProductFamilyModule),
    canActivate: [AuthGuard],
    data: {
      code: "product_family_management",
      text: "fa-building",
      iconName: "fa-building",
      iconFontSet: "fa",
    },
  },
  {
    path: "groups",
    loadChildren: () =>
      import(
        "./layout-modules/product-group-Management/product-group.module"
      ).then((m) => m.ProductGroupModule),
    canActivate: [AuthGuard],
    data: {
      code: "product_group_management",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "grades",
    loadChildren: () =>
      import("./layout-modules/grades-Management/grades.module").then(
        (m) => m.GradesModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "grade_management",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "tenants",
    loadChildren: () =>
      import("./layout-modules/tenant-Management/tenant-config.module").then(
        (m) => m.TenantConfigModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "tenant_management",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "tenant-current",
    loadChildren: () =>
      import("./layout-modules/tenant-current-Management/tenant-current.module").then(
        (m) => m.TenantCurrentModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "tenant_current_management",
      text: "fa-layer-group",
      iconName: "fa-gear",
      iconFontSet: "fa",
    },
  },
  {
    path: "pos-cards",
    loadChildren: () =>
      import("./layout-modules/pos-Management/pos-card.module").then(
        (m) => m.PosCardModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "card_actions",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "dictionary",
    loadChildren: () =>
      import("./layout-modules/dictionary-Management/dictionary.module").then(
        (m) => m.DictionaryModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "dictionary_management",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "service-modes",
    loadChildren: () =>
      import(
        "./layout-modules/service-modes-Management/service-modes.module"
      ).then((m) => m.ServiceModeModule),
    canActivate: [AuthGuard],
    data: {
      code: "service_mode_management",
    },
  },
  {
    path: "fueling-modes",
    loadChildren: () =>
      import(
        "./layout-modules/fueling-mode-Management/fueling-mode.module"
      ).then((m) => m.FuelingModeModule),
    canActivate: [AuthGuard],
    data: {
      code: "fuelling_mode_management",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "locales",
    loadChildren: () =>
      import("./layout-modules/locales-Management/locales.module").then(
        (m) => m.LocaleModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "locale_management",
      text: "fa-layer-group",
      iconName: "fa-layer-group",
      iconFontSet: "fa",
    },
  },
  {
    path: "playlists",
    loadChildren: () =>
      import("./layout-modules/playlists-Management/playlists.module").then(
        (m) => m.PlaylistsModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "multimedia_playlist_management",
      text: "fa-file-video",
      iconName: "fa-file-video",
      iconFontSet: "fa",
    },
  },
  {
    path: "multimedia-devices",
    loadChildren: () =>
      import("./layout-modules/multimedia-devices-Management/multimedia-devices.module").then(
        (m) => m.MultimediaDevicesModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "multimedia_device_management",
      text: "fa-tv",
      iconName: "fa-tv",
      iconFontSet: "fa",
    },
  },
  {
    path: "taxes",
    loadChildren: () =>
      import("./layout-modules/taxes-Management/taxes.module").then(
        (m) => m.TaxesModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "tax_management",
      text: "fa-percent",
      iconName: "fa-percent",
      iconFontSet: "fa",
    },
  },
  {
    path: "regions",
    loadChildren: () =>
      import("./layout-modules/regions-Management/regions.module").then(
        (m) => m.RegionsModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "region_management",
      text: "fa-map",
      iconName: "fa-map",
      iconFontSet: "fa",
    },
  },
  {
    path: "units",
    loadChildren: () =>
      import("./layout-modules/units-Management/units.module").then(
        (m) => m.UnitsModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "product_unit_management",
      text: "fa-balance-scale",
      iconName: "fa-balance-scale",
      iconFontSet: "fa",
    },
  },
  {
    path: "payment-modes",
    loadChildren: () =>
      import("./layout-modules/payment-modes-Management/payment-modes.module").then(
        (m) => m.PaymentModesModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "payment_modes_management",
      text: "fa-creadit-card",
      iconName: "fa-creadit-card",
      iconFontSet: "fa",
    },
  },
  {
    path: "receipts",
    loadChildren: () =>
      import("./layout-modules/receipts-Management/receipts.module").then(
        (m) => m.ReceiptsModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "receipt_management",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "entities",
    loadChildren: () =>
      import("./layout-modules/entity-Management/entity.module").then(
        (m) => m.EntityModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "entity_management",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "entity-current",
    loadChildren: () =>
      import("./layout-modules/entity-current-Management/entity-current.module").then(
        (m) => m.EntityCurrentModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "entity_current_management",
      text: "fa-layer-group",
      iconName: "fa-gear",
      iconFontSet: "fa",
    },
  },
  {
    path: "manual-transaction",
    loadChildren: () =>
      import("./layout-modules/manual-transaction-management/manual-transaction.module").then(
        (m) => m.ManualTransactionModule
      ),
    canActivate: [AuthGuard],
    data: {
      code: "fleet_manual_transaction",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "devices",
    loadChildren: () =>
    import("./layout-modules/devices-Management/devices.module").then(
      (m) => m.DevicesModule
    ),
    canActivate: [AuthGuard],
    data: {
      code: "device_management",
      text: "fa-file",
      iconName: "fa-file",
      iconFontSet: "fa",
    },
  },
  {
    path: "entity-current-manager",
    data: {
      code: "entity_current_management",
    },
    component: PageNotFoundComponent
  },
  { path: "**", component: PageNotFoundComponent }, // must always be last
];
// directions_bus
@NgModule({
  imports: [
    RouterModule.forRoot(routes, { relativeLinkResolution: "legacy" }),
    ErrorRoutingModule,
  ],
  exports: [RouterModule, ErrorRoutingModule],
})
export class AppRoutingModule {
  constructor(private translate: TranslateService) {}
}
